import { useMediaQuery, useTheme } from '@mui/material';
import { addBusinessDays, format } from 'date-fns';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { memo, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { accountBalanceServices } from '!core/redux';
import { accountBalanceSelectedSelector } from '!core/redux/accountBalance/accountBalance.selectors';
import { isOtpActive } from '!core/redux/dbConfigurations';
import {
  securitiesAccountsSelector,
  securityAccountSelectedSelector,
} from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { tradingSlice } from '!views/index';
import { getSelectedFundsManagerTitle } from '!views/Market/Funds/redux';
import { getSelectedTitle } from '!views/Market/StockExchange/marketStockExchange.seletors';
import { marketStockExchangeServices } from '!views/Market/StockExchange/marketStockExchange.services';
import { getSelectedWalletTitle } from '!views/Wallet/wallet.selectors';
import { TrlOuterModal } from '»components/atoms';
import { ButtonMain } from '»components/atoms/Buttons';
import { DialogStyled, PaperStyled } from '»components/atoms/TrlOuterModal';
import { Otp } from '»components/Auth';
import { TrlDisclaimer } from '»components/molecules/TrlDisclaimer';
import { TrlErrorContainer } from '»components/molecules/TrlErrorContainer';
import { TrlNewOrderDataContainer } from '»components/molecules/TrlNewOrderDataContainer';
import { TrlStepper } from '»components/molecules/TrlStepper';
import { TrlStockExchangeMarketDesc } from '»components/molecules/TrlStockExchangeMarketDesc';
import { TrlStockExchangeMarketInputData } from '»components/molecules/TrlStockExchangeMarketInputData';
import { trlStockExchangeMarketInputDataServices } from '»components/molecules/TrlStockExchangeMarketInputData/TrlStockExchangeMarketInputData.services';
import { formatBalanceAccounts } from '»utils/accounts';
import {
  useCurrentPage,
  useHandleSagaError,
  useNegModalContext,
} from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { getFundType } from '»utils/funds';
import { TrlFormattedMessagePlain, TrlFormattedNumberPlain } from '»utils/i18n';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';
import { getOperationLabel } from '»utils/trading/trading.utils';

import {
  insertOrder,
  orderCalcComissions,
  replaceOrder,
  validateCode,
} from './TrlInsertOrderModal.helper';
import {
  fundsOrdTypeList,
  getAvailableFundsOrdTypes,
  getAvailableOrdTypes,
  getAvailablePriceTypes,
} from './TrlInsertOrderModal.helper.selectors';
import {
  handleBack,
  handleComplete,
  handleStep,
  isLastStep,
} from './TrlInsertOrderModal.helper.steps';
import {
  ButtonsContainerStyled,
  FormStyled,
} from './TrlInsertOrderModal.styled';
import { trlInsertOrderValidation } from './TrlInsertOrderModal.yup';

const currentDay = new Date();

export const TrlInsertOrderModal = memo(({ titleInfo }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [isValidRequest, setIsValidRequest] = useState(true);
  const [invalidRequestReason, setInvalidRequestReason] = useState(null);

  const [orderComissions, setOrderComissions] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);

  const [quantityAvailable, setQuantityAvailable] = useState(0);

  const [stockExchangeOrdTypesList, setStockExchangeOrdTypesList] = useState(
    [],
  );
  const [priceTypesList, setPriceTypesList] = useState([]);
  const [orderPossibilities, setOrderPossibilities] = useState([]);
  const [balanceAccounts, setBalanceAccounts] = useState({});

  const formRef = useRef(null);

  const {
    isFunds: isFundsPage,
    isMarketStockExchangeOrders,
    isWalletPage,
  } = useCurrentPage();
  const { handleSagaError } = useHandleSagaError();

  const selectedFund = useSelector(getSelectedFundsManagerTitle);
  const selectedTitleStockMarket = useSelector(getSelectedTitle);
  const selectedTitleWallet = useSelector(getSelectedWalletTitle);
  const selectedTitle =
    titleInfo ??
    (isFundsPage
      ? selectedFund
      : isWalletPage
        ? selectedTitleWallet
        : selectedTitleStockMarket);
  const securityAccounts = useSelector(securitiesAccountsSelector);
  const currentSecurityAccount = useSelector(securityAccountSelectedSelector);
  const currentBalanceAccount = useSelector(accountBalanceSelectedSelector);
  const useOtpForStockExchange = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_STOCK_ORDER'),
  );
  const useOtpForFundsManager = useSelector((state) =>
    isOtpActive(state, 'ENFORCE_OTP_FUNDS_MANAGER'),
  );

  const isFundTitle = !!getFundType(selectedTitle);

  const useOtp = isFundTitle ? useOtpForFundsManager : useOtpForStockExchange;

  const {
    isInsertOrderModalOpened,
    setIsInsertOrderModalOpened,
    defaultOperationType,
  } = useNegModalContext();

  const {
    exchange,
    securityType,
    symbol,
    lastDateTime,
    priceRepresentation,
    descr,
    securityDescription,
    exchangeDescr,
    quantityRepresentation,
    nominalValue,
    lastPx,
    instrument,
    country,
    countryDescr,
    totalVolumeTraded,
    variationPctg,
    localStarTtime,
    localEndTime,
    currency,
    currencySymbol,
    chartData,
    canTrade,
    isinCode,
    isin,
    roundPrice,
    quantityPrecision,
    ordExpireNumDays,
    initialAmount,
    additionalAmount,
    orderQty,
    price,
    expireTime,
    side,
    stopPx,
    ordType: ordExecutionType,
    ordBookKey,
    timeInForce,
    subscriptionType,
  } = selectedTitle;

  const availableFundsOrdTypes = getAvailableFundsOrdTypes(subscriptionType);

  const orderRules = {
    // [ordType, priceType]: ordExecutionType
    '1-1': 1,
    '1-2': 2,
    '2-1': 3,
    '2-2': 4,
  };

  const ordExecution = {
    getOrdExecutionType: (selectOrderType, selectPriceType) => {
      const key = `${selectOrderType}-${selectPriceType}`;
      return orderRules[key] || null;
    },
    getOrdAndPriceType: (currentOrdExecutionType) => {
      for (const [index, [key, value]] of Object.entries(
        orderRules,
      ).entries()) {
        if (value === Number(currentOrdExecutionType)) {
          const [ordType, priceType] = key.split('-');

          return { ordType: Number(ordType), priceType: Number(priceType) };
        } else if (index === Object.entries(orderRules).length - 1) {
          return { ordType: null, priceType: null };
        }
      }
    },
  };

  const { ordType, priceType } =
    ordExecution.getOrdAndPriceType(ordExecutionType);

  const initialOperation = isMarketStockExchangeOrders
    ? Number(side)
    : defaultOperationType;
  const initialSelectOrderType = isFundTitle
    ? initialOperation === 'E'
      ? 3
      : availableFundsOrdTypes[0]?.value
    : isMarketStockExchangeOrders
      ? ordType
      : stockExchangeOrdTypesList.length > 0 &&
        stockExchangeOrdTypesList.filter((ord) => ord.show)[0].value;
  const initialSelectPriceType = isMarketStockExchangeOrders
    ? priceType
    : priceTypesList.length > 0 &&
      priceTypesList.filter((price) => price.show)[0].value;
  const initialInputQuantity = isMarketStockExchangeOrders ? orderQty : '';
  const initialInputPrice = isMarketStockExchangeOrders ? (price ?? '') : '';
  const initialInputStopPrice = stopPx ?? '';

  const getInitialSelectedBalanceAccount = () => {
    if (Object.keys(balanceAccounts).length === 0) return '';

    const currentAccountCanNegotiate = !!balanceAccounts[
      currentSecurityAccount
    ].find((account) => account.value === currentBalanceAccount);

    if (currentAccountCanNegotiate) return currentBalanceAccount;

    const currentBalanceAccounts = balanceAccounts[currentSecurityAccount];

    if (currentBalanceAccounts.length > 0)
      return currentBalanceAccounts[0].value;

    return '';
  };

  const initialExpirationDate = isFundTitle
    ? addBusinessDays(currentDay, ordExpireNumDays)
    : isMarketStockExchangeOrders
      ? expireTime
      : initialSelectPriceType === 1
        ? format(currentDay, "yyyy-MM-dd'T'00:00:00'Z'")
        : '';

  const decimalDigits = getDecimalDigits(roundPrice);

  const getBalanceAccountCurrency = (securityAccount, balanceAccount) => {
    const balanceAccCurrency = balanceAccounts[securityAccount]?.find(
      (account) => account.value === balanceAccount,
    )?.currency;

    return balanceAccCurrency;
  };

  const steps = [
    TrlFormattedMessagePlain({ id: 'TRLT_UI-INSERTION' }),
    TrlFormattedMessagePlain({ id: 'TRLT_UI-SIMULATION' }),
    TrlFormattedMessagePlain({ id: 'TRLT_UI-CONFIRMATION' }),
  ];

  const getQuantityAvailable = () => {
    trlStockExchangeMarketInputDataServices
      .portInstrBalance({
        account: currentSecurityAccount,
        symbol,
        exchange,
        securityType,
      })
      .then((resp) => {
        if (resp.length > 0) {
          setQuantityAvailable(resp[0].avaiQuantity);
        } else if (resp.length === 0 && quantityAvailable !== 0) {
          setQuantityAvailable(0);
        }
      })
      .catch((error) => {
        handleSagaError(error);
      });
  };

  const handleGetOptError = (event) => {
    setInvalidRequestReason(event);
  };

  useEffect(() => {
    formRef.current?.scrollTo(0, 0);
  }, [activeStep]);

  useEffect(() => {
    if (isInsertOrderModalOpened) getQuantityAvailable();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsertOrderModalOpened]);

  useEffect(() => {
    if (isInsertOrderModalOpened)
      marketStockExchangeServices
        .exchangePriceTypes({ exchange })
        .then((resp) => {
          const possibilities = resp.reduce((acc, ord) => {
            const typeAlreadyAdded = acc.find((el) => el === ord.fixTypePrice);

            if (!typeAlreadyAdded) acc.push(ord.fixTypePrice);

            return acc;
          }, []);

          setOrderPossibilities(possibilities);
          if (!isFundTitle)
            setStockExchangeOrdTypesList(getAvailableOrdTypes(possibilities));
          setPriceTypesList(getAvailablePriceTypes(possibilities));
        })
        .catch((error) => {
          handleSagaError(error);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange, isInsertOrderModalOpened]);

  const getAccounts = async () => {
    const accounts = {};

    for (const secAccount of securityAccounts) {
      const secAccountValue = secAccount.value;

      const resp = await accountBalanceServices.accountBalance(
        secAccountValue,
        currency,
      );

      const currentBalanceAccounts = formatBalanceAccounts(resp);

      accounts[secAccountValue] = currentBalanceAccounts;
    }

    setBalanceAccounts(accounts);
  };

  useEffect(() => {
    if (isInsertOrderModalOpened && currentSecurityAccount && currency)
      getAccounts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsertOrderModalOpened]);

  const handleCloseModal = () => {
    setIsInsertOrderModalOpened(false);
    setCompleted({});
    setIsValidRequest(true);
    setInvalidRequestReason(null);
    setOrderComissions(null);
    setOrderStatus(null);
    if (activeStep !== 0) handleStep({ step: 0, setActiveStep });
    if (isMarketStockExchangeOrders && activeStep === 2)
      dispatch(
        tradingSlice.actions.tradingDailyStockExchangeRequest({
          getEvents: !isMobile,
        }),
      );
  };

  const getPriceFinalValue = (price, selectPriceType) =>
    selectPriceType === 1 ? null : price;

  const getStopPriceFinalValue = (stopPrice, selectOrderType) =>
    selectOrderType === 1 ? null : stopPrice;

  const handleSubmit = async (values, { setSubmitting, setFieldValue }) => {
    setIsValidRequest(false);
    setInvalidRequestReason(null);

    const {
      selectSecurityAccount,
      selectBalanceAccount,
      inputQuantity,
      inputPrice,
      selectOperation,
      selectOrderType,
      selectPriceType,
      selectExpirationDate,
      inputStopPrice,
      validationCode,
    } = values;

    if (selectOrderType === 1 && inputStopPrice !== '')
      setFieldValue('inputStopPrice', '');

    if (selectPriceType === 1 && inputPrice !== '')
      setFieldValue('inputPrice', '');

    const price = getPriceFinalValue(inputPrice, selectPriceType);
    const stopPrice = getStopPriceFinalValue(inputStopPrice, selectOrderType);

    const submissionOrdType = ordExecution.getOrdExecutionType(
      selectOrderType,
      selectPriceType,
    );

    const balanceAccountCurrency = getBalanceAccountCurrency(
      selectSecurityAccount,
      selectBalanceAccount,
    );

    if (activeStep === 0) {
      const orderCalcComissionsResp = await orderCalcComissions({
        orderQty: inputQuantity,
        price,
        stopPx: stopPrice,
        clientId: selectSecurityAccount,
        cashAccount: selectBalanceAccount,
        securityExchange: exchange,
        securityType,
        symbol,
        currency,
        side: selectOperation,
      });

      const { isValid, comissions, reason } = orderCalcComissionsResp;

      if (isValid) {
        setIsValidRequest(isValid);
        setOrderComissions(comissions);
        handleComplete({
          steps,
          completed,
          activeStep,
          setCompleted,
          setActiveStep,
        });
      } else {
        setIsValidRequest(isValid);
        setInvalidRequestReason(reason);
      }
      setSubmitting(false);
    } else {
      const [{ simulationId }] = orderComissions;
      let negOrderResp = {};
      let canExecuteOrder = true;

      if (useOtp)
        canExecuteOrder = await validateCode({
          validationCode,
          setIsValidRequest,
          setInvalidRequestReason,
          handleSagaError,
        });

      if (canExecuteOrder) {
        if (isMarketStockExchangeOrders) {
          negOrderResp = await replaceOrder({
            simulationId,
            clientId: selectSecurityAccount,
            ordBookKey: ordBookKey,
            orderQty: inputQuantity,
            ordType: submissionOrdType,
            price,
            timeInForce,
            expireTime: selectExpirationDate,
            stopPx: stopPrice,
            confirmSeq: orderStatus && orderStatus[0].confirmSeq,
          });
        } else {
          negOrderResp = await insertOrder({
            simulationId,
            clientId: selectSecurityAccount,
            cashAccount: selectBalanceAccount,
            securityExchange: exchange,
            securityType,
            symbol,
            ordType: submissionOrdType,
            side: selectOperation,
            orderQty: inputQuantity,
            currency: balanceAccountCurrency,
            price,
            stopPx: stopPrice,
            timeInForce: '6',
            expireTime: selectExpirationDate,
            confirmSeq: orderStatus && orderStatus[0].confirmSeq,
            isin,
            isFunds: isFundTitle,
          });
        }

        const { isValid, order, reason } = negOrderResp;

        if (isValid) {
          setIsValidRequest(isValid);
          setOrderStatus(order);
          handleComplete({
            steps,
            completed,
            activeStep,
            setCompleted,
            setActiveStep,
          });
        } else {
          setIsValidRequest(isValid);
          setInvalidRequestReason(reason);
          if (order) setOrderStatus(order);
        }
      }

      setSubmitting(false);
    }
  };

  const getBottomButtons = (isSubmitting, dirty, errors, setFieldValue) => {
    const lastStep = isLastStep({ activeStep, steps });

    const isSubmitButtonEnabled = () => {
      if (!isSubmitting && dirty) {
        if (
          activeStep === 0 &&
          (Object.keys(errors).length === 0 ||
            (Object.keys(errors).length === 1 && errors.validationCode))
        )
          return true;

        if (activeStep === 1) {
          if (useOtp) {
            if (Object.keys(errors).length === 0) return true;
          } else if (
            Object.keys(errors).length === 0 ||
            (Object.keys(errors).length === 1 && errors.validationCode)
          )
            return true;
        }
      }

      return false;
    };

    return (
      <ButtonsContainerStyled>
        {activeStep === 0 && (
          <ButtonMain
            onClick={handleCloseModal}
            variant="secondary"
            expressionId="TRLT_UI-CANCEL"
            mr="16px"
          />
        )}
        {activeStep !== 0 && !lastStep && (
          <ButtonMain
            disabled={activeStep === 0}
            onClick={() => {
              handleBack({ setActiveStep });
              setFieldValue('validationCode', '');
            }}
            variant="secondary"
            expressionId="TRLT_UI-BACK"
            mr={2}
          />
        )}
        {!lastStep && (
          <ButtonMain
            disabled={!isSubmitButtonEnabled()}
            isLoading={isSubmitting}
            expressionId="TRLT_UI-NEXT"
            type="submit"
            id="submit"
          />
        )}
      </ButtonsContainerStyled>
    );
  };

  const getStepContent = ({ step, values }) => {
    const orderTypeLabel = (
      isFundTitle ? fundsOrdTypeList : stockExchangeOrdTypesList
    ).find((ord) => ord.value === values.selectOrderType)?.label;

    const priceTypeLabel = priceTypesList.find(
      (price) => price.value === values.selectPriceType,
    )?.label;

    const operationLabel = getOperationLabel(values.selectOperation);

    const expirationDateElement = (
      <TrlDateFormatted date={values.selectExpirationDate} />
    );

    const lastDateTimeElement = lastDateTime ? (
      <>
        <TrlDateFormatted date={lastDateTime} />
        {' | '}
        <TrlTimeFormatted
          value={lastDateTime}
          hour="numeric"
          minute="numeric"
          second="numeric"
        />
      </>
    ) : null;

    let grossAmount = 0;
    let comission = 0;
    let accruedInterest = 0;
    let exchangeRate = 0;
    let netAmount = 0;

    if (orderComissions) {
      grossAmount = orderComissions[0].grossAmount;
      comission = orderComissions[0].comission;
      accruedInterest = orderComissions[0].accruedInterest;
      exchangeRate = orderComissions[0].exchangeRate;
      netAmount = orderComissions[0].netAmount;
    }

    const endDecorator = getPercentageDecorator(priceRepresentation);

    const balanceAccountCurrency = getBalanceAccountCurrency(
      values.selectSecurityAccount,
      values.selectBalanceAccount,
    );

    const selectSecurityAccountDescr = securityAccounts.find(
      (secAccount) => secAccount.value === values.selectSecurityAccount,
    ).label;

    const dataToDisplay = [
      {
        label: 'TRLT_UI-CLIENT',
        elements: [
          {
            label: 'TRLT_UI-CUSTODIAN_ACCOUNT',
            value: selectSecurityAccountDescr,
          },
          {
            label: 'TRLT_UI-CURRENT_ACCOUNT',
            value: values.selectBalanceAccount,
          },
        ],
      },
      {
        label: 'TRLT_UI-SECURITY',
        elements: [
          {
            label: 'TRLT_UI-ISIN',
            value: isinCode || isin,
          },
          {
            label: 'TRLT_UI-MARKET',
            value: exchangeDescr,
          },
          {
            label: 'TRLT_UI-NAME',
            value: descr || securityDescription,
          },
        ],
      },
      {
        label: 'TRLT_UI-ORDER_DATA',
        elements: [
          {
            label: 'TRLT_UI-OPERATION',
            value: <FormattedMessage id={operationLabel} />,
          },
          {
            label: 'TRLT_UI-ORDER',
            value: <FormattedMessage id={orderTypeLabel} />,
          },
          {
            label: 'TRLT_UI-PRICE_TYPE',
            value: <FormattedMessage id={priceTypeLabel} />,
          },
          {
            label: 'TRLT_UI-PRICE',
            value: (
              <TrlNumberFormatted
                value={getPriceFinalValue(
                  values.inputPrice,
                  values.selectPriceType,
                )}
                endDecorator={endDecorator}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-PRICE_STOP',
            value: (
              <TrlNumberFormatted
                value={getStopPriceFinalValue(
                  values.inputStopPrice,
                  values.selectOrderType,
                )}
                endDecorator={endDecorator}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-QUANTITY',
            value: values.inputQuantity,
          },
          {
            label: 'TRLT_UI-EXPIRATION_DATE',
            value: expirationDateElement,
          },
          {
            label: 'TRLT_UI-NOMINAL_VALUE',
            value: (
              <TrlNumberFormatted
                value={displayNominalValue(
                  quantityRepresentation,
                  priceRepresentation,
                  nominalValue,
                )}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-LAST_QUOTATION',
            value: (
              <TrlNumberFormatted
                value={lastPx}
                decimalDigits={decimalDigits}
                endDecorator={endDecorator}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-SETTLEMENT_CURRENCY',
            value: balanceAccountCurrency,
          },
          {
            label: 'TRLT_UI-DATE',
            value: lastDateTimeElement,
          },
        ],
      },
      {
        label: 'TRLT_UI-CHARGES',
        elements: [
          {
            label: 'TRLT_UI-ESTIMATED_ORDER_VALUE',
            value: (
              <TrlNumberFormatted
                value={grossAmount}
                endDecorator={` ${balanceAccountCurrency}`}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-ESTIMATED_OPERATION_COSTS',
            value: (
              <TrlNumberFormatted
                value={comission}
                endDecorator={` ${balanceAccountCurrency}`}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
          {
            label: 'TRLT_UI-ESTIMATED_TRANSACTION_VALUE',
            value: (
              <TrlNumberFormatted
                value={netAmount}
                endDecorator={` ${balanceAccountCurrency}`}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          },
        ],
      },
    ];

    if (isFundTitle)
      dataToDisplay.forEach((item) => {
        if (item.label === 'TRLT_UI-ORDER_DATA') {
          item.elements = item.elements.filter(
            (element) =>
              element.label !== 'TRLT_UI-PRICE_TYPE' &&
              element.label !== 'TRLT_UI-PRICE_STOP' &&
              element.label !== 'TRLT_UI-NOMINAL_VALUE' &&
              element.label !== 'TRLT_UI-PRICE',
          );
        }
      });

    if (accruedInterest)
      dataToDisplay.forEach((item) => {
        if (item.label === 'TRLT_UI-CHARGES')
          item.elements.splice(2, 0, {
            label: 'TRLT_UI-VALUE_INTEREST',
            value: (
              <TrlNumberFormatted
                value={accruedInterest}
                endDecorator={` ${balanceAccountCurrency}`}
                decimalDigits={decimalDigits}
                fontFamily="Roboto"
              />
            ),
          });
      });

    if (balanceAccountCurrency !== currency)
      dataToDisplay.forEach((item) => {
        if (item.label === 'TRLT_UI-CHARGES')
          item.elements.splice(-1, 0, {
            label: 'TRLT_UI-EXCHANGE_RATE',
            value: exchangeRate,
          });
      });

    switch (step) {
      case 0:
        return (
          <>
            <TrlStockExchangeMarketDesc
              instrument={instrument}
              descr={descr}
              securityDescription={securityDescription}
              symbol={symbol}
              exchange={exchange}
              exchangeDescr={exchangeDescr}
              country={country}
              lastPx={lastPx}
              lastDateTime={lastDateTime}
              totalVolumeTraded={totalVolumeTraded}
              nominalValue={nominalValue}
              variationPctg={variationPctg}
              priceRepresentation={priceRepresentation}
              quantityRepresentation={quantityRepresentation}
              securityType={securityType}
              localStartTime={localStarTtime}
              localEndTime={localEndTime}
              countryDescr={countryDescr}
              currency={currency}
              chartData={chartData}
              canTrade={canTrade}
              roundPrice={decimalDigits}
            />
            <TrlStockExchangeMarketInputData
              quantityAvailable={quantityAvailable}
              orderPossibilities={orderPossibilities}
              instrumentInfo={selectedTitle}
              balanceAccounts={balanceAccounts}
            />
          </>
        );
      case 1:
        return (
          <>
            <TrlNewOrderDataContainer
              data={dataToDisplay}
              activeStep={activeStep}
            />
            {useOtp && (
              <Otp
                margin="16px 0 0 0"
                textFieldName="validationCode"
                onError={handleGetOptError}
                ordType={values.selectOperation}
                symbol={symbol}
                orderQty={values.inputQuantity}
                price={
                  values.inputPrice
                    ? TrlFormattedNumberPlain(
                        Number(values.inputPrice),
                        decimalDigits,
                      )
                    : '-'
                }
                amount={TrlFormattedNumberPlain(grossAmount, decimalDigits)}
                currency={currency}
                currencySymbol={currencySymbol}
                isTighter
              />
            )}
            <TrlDisclaimer
              disclaimerId={isFundTitle ? 'SIM_FUND_BOLSA' : 'SIM_BOLSA'}
              operationType={values.selectOperation === 1 ? 'B' : 'S'}
              symbol={symbol}
              exchange={exchange}
              securityType={securityType}
              isModal
            />
          </>
        );
      case 2:
        return (
          <>
            <TrlDisclaimer
              expressionId="TRLT_UI-ORDER_NUMBER"
              expressionParams={{
                ordBookKey: orderStatus[0].ordBookKey,
              }}
              isModal
            />
            <TrlNewOrderDataContainer
              data={dataToDisplay}
              activeStep={activeStep}
            />
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <DialogStyled
      open={isInsertOrderModalOpened}
      onClose={handleCloseModal}
      PaperComponent={PaperStyled}
    >
      <TrlOuterModal title="TRLT_UI-NEW_ORDER" handleClose={handleCloseModal}>
        <>
          <TrlErrorContainer
            showContainer={!!(!isValidRequest && invalidRequestReason)}
            errorMessage={invalidRequestReason}
            modalStatus={isInsertOrderModalOpened}
          />
          <TrlStepper
            activeStep={activeStep}
            steps={steps}
            completed={completed}
            isModal
          />
          <Formik
            initialValues={{
              selectSecurityAccount: currentSecurityAccount,
              selectBalanceAccount: getInitialSelectedBalanceAccount(),
              selectOrderType: initialSelectOrderType,
              selectOperation: initialOperation,
              inputQuantity: initialInputQuantity,
              selectPriceType: initialSelectPriceType,
              inputPrice: initialInputPrice,
              selectExpirationDate: initialExpirationDate,
              inputStopPrice: initialInputStopPrice,
              validationCode: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={trlInsertOrderValidation({
              quantityAvailable,
              activeStep,
              useOtp,
              instrumentCurrency: currency,
              isFunds: isFundTitle,
              quantityPrecision,
              initialAmount,
              additionalAmount,
              lastPx,
            })}
            enableReinitialize
          >
            {({ ...rest }) => (
              <FormStyled ref={formRef} activeStep={activeStep}>
                {getStepContent({
                  step: activeStep,
                  values: rest.values,
                })}
                {activeStep !== 2 &&
                  getBottomButtons(
                    rest.isSubmitting,
                    rest.dirty,
                    rest.errors,
                    rest.setFieldValue,
                  )}
              </FormStyled>
            )}
          </Formik>
        </>
      </TrlOuterModal>
    </DialogStyled>
  );
});

TrlInsertOrderModal.displayName = 'TrlInsertOrderModal';

TrlInsertOrderModal.propTypes = {
  titleInfo: PropTypes.object,
};
